import { useQuery } from "@tanstack/react-query";
import { QUERY_OPTIONS } from "../../../../../utils/config";
import { getEvaluationQuestions, getEvaluationResponses, getLikertScaleLabels, getPanopticScoringKeywords, getProjectDetails, getProjectEvaluators } from "./_request";
import { ProjectDetails } from "./_models";

export const useProjectDataQuery = (projectId: number) => {
    const query = useQuery({
        queryKey: ["project-data", projectId],
        queryFn: async ({ signal }) => {
            const response = await getProjectDetails(projectId, signal);

            if (response.status !== "ok") {
                throw new Error(
                    response.message || "Failed to fetch project details"
                );
            }

            return response.data;
        },
        enabled: !!projectId,
        ...QUERY_OPTIONS,
    });

    return {
        projectDetails: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
};


export const useProjectEvaluatorsQuery = (projectId: number, user_id: number) => {
    const query = useQuery({
        queryKey: ["project-evaluators", `${projectId}-${user_id}`],
        queryFn: async ({ signal }) => {
            const response = await getProjectEvaluators(projectId, signal);

            if (response.status !== "ok") {
                throw new Error(
                    response.message || "Failed to fetch project evaluators"
                );
            }

            return response.data?.evaluators ?? [];
        },
        enabled: !!projectId,
        ...QUERY_OPTIONS,
    });

    return {
        projectEvaluators: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
};

export const useEvaluationQuestionsQuery = (projectId: number) => {
    const query = useQuery({
        queryKey: ["evaluation-questions", projectId],
        queryFn: async ({ signal }) => {
            const response = await getEvaluationQuestions(projectId, signal);

            if (response.status !== "ok") {
                throw new Error(
                    response.message || "Failed to fetch evaluation questions"
                );
            }

            return response.data?.questions ?? [];
        },
        enabled: !!projectId,
        ...QUERY_OPTIONS,
    });

    return {
        evaluationQuestions: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
};


export const usePanopticScoringKeyWordAndLikertScaleQuery = () => {
    const query = useQuery({
        queryKey: ["panoptic-scoring-keywords", "likert-scale-labels"],
        queryFn: async ({ signal }) => {
            const [scoringResponse, likertScaleResponse] = await Promise.all([
                getPanopticScoringKeywords(signal),
                getLikertScaleLabels(signal),
            ]);

            if (scoringResponse.status !== "ok") {
                throw new Error(
                    scoringResponse.message ||
                    "Failed to fetch panoptic scoring keywords"
                );
            }

            if (likertScaleResponse.status !== "ok") {
                throw new Error(
                    likertScaleResponse.message ||
                    "Failed to fetch Likert scale labels"
                );
            }

            return {
                scoringKeywords: scoringResponse.data ?? [],
                likertScaleLabels: likertScaleResponse.data ?? [],
            };
        },
        ...QUERY_OPTIONS,
    });

    return {
        scoringKeywords: query.data?.scoringKeywords ?? [],
        likertScaleLabels: query.data?.likertScaleLabels ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
};

export const useEvaluationResponsesQuery = (
    projectId: number,
    evaluatorId?: number
) => {
    return useQuery({
        queryKey: ["evaluation-responses", projectId, evaluatorId],
        enabled: !!evaluatorId,
        queryFn: async ({ signal }) => {
            const response = await getEvaluationResponses(
                projectId,
                evaluatorId!,
                signal
            );

            if (response.status !== "ok") {
                throw new Error(
                    response.message || "Failed to fetch evaluation responses"
                );
            }

            return response.data;
        },
        staleTime: 1000 * 60 * 5,
    });
};